import _ from "lodash";
import {displayErrorSnackBar} from "../../components/SnackbarProvider/actions";
import {getErrorMessage} from "../../utils/utils";
import {
  ASSETS_LIABILITIES_TYPES,
  DEPOT_ASSET_TYPE,
  GROUPED_ASSET_CATEGORIES, GROUPED_LIABILITIES_CATEGORIES,
  SERVER_ERROR, EXTERNAL_CONTRACTS_IDS, FILTER_OPTIONS,
} from "./constants";

export const getErrorMsg = (dispatch, error, setFormDataErrors) => {
  const status = _.get(error, 'status');

  if(status === 400 && _.isFunction(setFormDataErrors)){
    setFormDataErrors(error && error.data || error || {});
  } else {
    dispatch(displayErrorSnackBar((!status || status === 404) ? getErrorMessage(error) : SERVER_ERROR))
  }
};

const buildLinkedItems = (items, links, type) => {
  const linkedItems = {};
  const groupedItems = {};

  Object.values(items || {}).forEach((dataForType) => {
    dataForType.assets.forEach(item => groupedItems[`${item.type_id}_${item.id}`] = {...item})
  });

  links.forEach(link => {
    let leftKey = `${link.asset_type}_${link.asset_id}`;
    let rightKey = `${link.liability_type}_${link.liability_id}`;

    if(type === ASSETS_LIABILITIES_TYPES.ASSETS){
      [leftKey, rightKey] = [rightKey, leftKey];  // swap direction
    }

    if(!linkedItems.hasOwnProperty(leftKey)){
      linkedItems[leftKey] = [];
    }

    const liability = groupedItems[rightKey];
    if(liability){
      linkedItems[leftKey].push(liability);
    }
  });

  return linkedItems;
};

const buildAsset = (groupedAssets, otherAssetType, otherAssetData) => {
  if(!otherAssetData.assets.length) return;  // ignore empty groups

  const category = GROUPED_ASSET_CATEGORIES[otherAssetType];

  groupedAssets[category.id] = {
    isGroup: category.isGroup,
    title: category.title,  // TODO: we could use otherAssetData.name from BE
    icon: category.reactIcon,
    id: category.id,
    ...otherAssetData,
    assets: _.orderBy(otherAssetData.assets, ['asset_value'], ['desc']),
    total_value: _.sumBy(otherAssetData.assets, 'asset_value')
  };
};

const addAssetsItems = (groupedAssets, data, withLinks=true) => {
  const liabilitiesForAssets = withLinks
    ? buildLinkedItems(data.data.liabilities, data.data.asset_liability_links, ASSETS_LIABILITIES_TYPES.LIABILITIES)
    : {};

  Object.entries(data.data.assets || {})
    .forEach(([otherAssetType, otherAssetData]) => buildAsset(
      groupedAssets, otherAssetType,
      {
        ...otherAssetData,
        assets: otherAssetData.assets.map(
          a => ({...a, linkedItems: liabilitiesForAssets[`${a.type_id}_${a.id}`] || []})
        )
      },
    ));
};

export const buildAssets = (data, depotsAndCash, withDepots=true, withCash=true, withAssets=true) => {
  const groupedAssets = {};
  let itemsForLinks = {};
  if (withAssets && !data.loading && !!data.data) {
    addAssetsItems(groupedAssets, data);

    itemsForLinks = getLiabilitiesItems(data);
  }

  if (!depotsAndCash.loading && !!depotsAndCash.data) {
    if(withCash) {
      Object.entries(depotsAndCash.data.assets || {})
        .forEach(([otherAssetType, otherAssetData]) => buildAsset(
          groupedAssets, otherAssetType, otherAssetData
        ));
    }
    if(withDepots) {
      Object.entries(depotsAndCash.data.portfolios || {})
        .forEach(([otherAssetType, otherAssetData]) => buildAsset(
          groupedAssets, otherAssetType, otherAssetData
        ));
    }
  }

  const dataForColors = data.data || depotsAndCash.data;

  return {
    items: _.orderBy(Object.values(groupedAssets), [(a) => a.id === DEPOT_ASSET_TYPE.id, 'total_value'], ['asc', 'desc']).map((category, index) => ({...category, color: dataForColors.ASSET_CATEGORIES_COLORS[index]})),
    total_value: _.sumBy(Object.values(groupedAssets), 'total_value'),
    itemsForLinks,
  };
};

const getLiabilitiesItems = (data, withLinks=true) => {
  const assetsForLiabilities = withLinks
    ? buildLinkedItems(data.data.assets, data.data.asset_liability_links, ASSETS_LIABILITIES_TYPES.ASSETS)
    : {};

  return _.orderBy(Object.entries(data.data.liabilities || {})
    .map(([otherAssetType, otherAssetData]) => {
      const category = GROUPED_LIABILITIES_CATEGORIES[otherAssetType];
      const assets = otherAssetData.assets.map(
        i => ({...i, linkedItems: assetsForLiabilities[`${i.type_id}_${i.id}`] || []})
      );

      return {
        title: category.title,
        icon: category.reactIcon,
        id: category.id,
        total_value: _.sumBy(assets, 'asset_value'),
        ...otherAssetData,
        assets,
      }
    }), ['total_value'], ['desc']);
};

export const buildLiabilities = (data) => {
  if (data.loading || !data.data) return {};

  const items = getLiabilitiesItems(data);

  const groupedAssets = {};
  addAssetsItems(groupedAssets, data, false);

  return {
    items: items.map((i, index) => ({...i, color: data.data.LIABILITY_CATEGORIES_COLORS[index]})),
    total_value: _.sumBy(items, 'total_value'),
    itemsForLinks: _.orderBy(Object.values(groupedAssets), 'total_value', 'desc'),
  }
};

/**
* @param items {Array}: list of assets/liabilities grouped by category
* @param selectedFilter {int}: FILTER_OPTIONS value
*/
export const getFilteredCategories = (items, selectedFilter) => {
  const filteredItems = [];
  const showOnlyFuture = selectedFilter === FILTER_OPTIONS.future.value;
  const showOnlyExpired = selectedFilter === FILTER_OPTIONS.expired.value;

  (items || []).forEach((category) => {
    const filteredCategoryAssets = category.assets.filter(
      asset => !!asset.is_expired === showOnlyExpired && !!asset.is_future === showOnlyFuture
    );

    if (filteredCategoryAssets.length) {
      filteredItems.push({
        ...category,
        assets: filteredCategoryAssets,
        total_value: (showOnlyExpired || showOnlyFuture) ? 0 : category.total_value  // NOTE: expired/future asset has no value
      })
    }
  });

  return filteredItems;
};

export const isExternalContract = (subCategoryId) => EXTERNAL_CONTRACTS_IDS.has(subCategoryId);

export const checkExternalContractsOption = (o) => {
  return isExternalContract(o.value)
    ? {
      ...o,
      disabled: true,
      info: `Bitte klicken Sie “Zurück”, wenn Sie ${o.label} auswählen möchten.`
    }
    : o
}

export const getListUrl = (customerId, mainCategoryId, subCategoryId) => {
  return isExternalContract(subCategoryId)
    ? `customer/${customerId}/${subCategoryId}/external-contracts/`
    : `customer/${customerId}/other-assets/?asset_type=${mainCategoryId}`
};

export const getDetailsUrl = (customerId, asset) => {
  return isExternalContract(asset.type_id)
    ? `customer/${customerId}/${asset.type_id}/external-contracts/${asset.id}/`
    : `customer/${customerId}/other-assets/${asset.id}/?asset_type=${asset.type_id}`
};